import type { ComponentType } from "react";
import ReactDOM from "react-dom";

import * as reviewFormModule from "shared-components/forms/review";
import SharedIntlProviderHOC from "shared-components/hocs/sharedIntlProviderHOC";
import { Modal } from "shared-components/modal/modal";

import type { CurrentReview } from "../../services/reviews.service";

type ReviewFormProps = {
  cbAction: (reviewId: string) => void;
  currentReview: number;
  fromHostHub?: boolean;
  review: CurrentReview;
  totalReviews: number;
  user: { SSOid?: string };
};

// `review.jsx` mixes ES imports with a CommonJS `exports.ReviewForm`, so the
// named export isn't visible to the type checker (babel resolves it at
// runtime). Pull it off the module object and apply the props it consumes.
const ReviewForm = (
  reviewFormModule as unknown as { ReviewForm: ComponentType<ReviewFormProps> }
).ReviewForm;

// jQuery + bootstrap power the legacy shared `Modal`; they are loaded globally
// across the app, so closing the modal goes through the same `.modal('hide')`
// path every other caller uses.
declare const $: (selector: string) => {
  modal: (action: string) => void;
  one: (event: string, handler: () => void) => void;
};

const MODAL_CONTAINER_ID = "modal-container";

type OpenHostHubReviewModalArgs = {
  onReviewSubmitted?: () => void;
  review: CurrentReview;
  ssoId?: string;
};

/**
 * Mounts the existing host review modal (`ReviewForm` inside the shared
 * `Modal`) into the global `#modal-container`, reusing the same flow guests and
 * hosts already use elsewhere. On successful submission it closes the modal and
 * notifies the caller so the recommended list can refresh.
 */
export const openHostHubReviewModal = ({
  onReviewSubmitted,
  review,
  ssoId,
}: OpenHostHubReviewModalArgs): void => {
  const container = document.getElementById(MODAL_CONTAINER_ID);
  if (!container) return;

  const closeModal = (): void => {
    $(`#${MODAL_CONTAINER_ID}`).modal("hide");
  };

  const handleReviewSubmitted = (): void => {
    closeModal();
    onReviewSubmitted?.();
  };

  // Unmount once the modal is fully hidden, regardless of how it was dismissed
  // (submit, close icon, backdrop click, or Escape — the last two are handled
  // internally by Bootstrap). `one` guarantees the teardown runs exactly once.
  $(`#${MODAL_CONTAINER_ID}`).one("hidden.bs.modal", () => {
    ReactDOM.unmountComponentAtNode(container);
  });

  ReactDOM.render(
    <SharedIntlProviderHOC>
      <Modal
        addCloseIcon
        body={
          <ReviewForm
            cbAction={handleReviewSubmitted}
            currentReview={1}
            fromHostHub
            review={review}
            totalReviews={1}
            user={{ SSOid: ssoId }}
          />
        }
        modalID="review-modal"
        modalSize="lg-size"
        modalType="standard"
        show
      />
    </SharedIntlProviderHOC>,
    container,
  );
};
